import { WagerTypeCodesEnum } from "@tvg/ts-types/Wager";
import { Dispatch } from "redux";
import { RaceProgram, RaceWagerType } from "@tvg/ts-types/Race";
import { setSelectionRequest } from "@tvg/desktop-bet/src/store/actions";
import BetUtils from "@tvg/utils/betSelection";
import { get, uniq } from "lodash";
import { RunnerBI } from "@tvg/pp/src/hooks/useGetRunnerFromBI";
import { Selections } from "../../types";

export const isDuplicate = (selections: String[], runner: RunnerBI) =>
  selections.some((item) => +item === +runner.biNumber);

export const getType: { [id: string]: number } = {
  win: 0,
  place: 1,
  show: 2
};

export const getACrossTheBoardLength: { [id: string]: number } = {
  [WagerTypeCodesEnum.WIN_PLACE]: 2,
  [WagerTypeCodesEnum.PLACE_SHOW]: 2,
  [WagerTypeCodesEnum.WIN_SHOW]: 2,
  [WagerTypeCodesEnum.WIN_PLACE_SHOW]: 3
};

export const getEmptySelectionsMatrix = (
  race: RaceProgram,
  selectedBetTypeCode: WagerTypeCodesEnum
): string[][] => {
  const activeWagerType = (race.wagerTypes || []).find(
    // @ts-ignore
    (wagerType) => wagerType.type.code === selectedBetTypeCode
  );
  // @ts-ignore
  return BetUtils.setMatrixSizeByColumnCount(
    get(activeWagerType, "columnCount", 1)
  );
};

export const parseSimpleBets = (betSelections: string[][]): Selections[] =>
  betSelections.map((selections: string[]) =>
    selections.map((selection: string) => ({
      number: selection,
      saddleColor: "",
      numberColor: ""
    }))
  );

export const getNumberOfSelections = (selections: string[][]) =>
  (selections || []).filter((valid) => valid).flat().length;

export const filterScratchedSelections = (
  selections: string[][],
  scratchesList: string[][],
  race: RaceProgram,
  races: RaceProgram[],
  selectedBetType: RaceWagerType
) => {
  // find the initialRaceIndex for the leg picks
  const initialRaceIndex = races.findIndex(
    (element: RaceProgram) => element.id === race.id
  );

  // pick the scratches from the selections ...
  const scratchSelections = [...scratchesList];

  const filteredSelections = (selections || []).map(
    (currentSelections: string[], index: number) =>
      currentSelections.filter((selection) => {
        const isAlternate = selection.endsWith("B");
        const currentRace =
          selectedBetType.legCount > 1 ? races[initialRaceIndex + index] : race;

        const bettingInterests = currentRace?.bettingInterests;

        const selectionBi = (bettingInterests || []).find((bettingInterest) => {
          const biNumber = bettingInterest.biNumber.toString();
          return (
            biNumber === selection ||
            (isAlternate && `${biNumber}B` === selection)
          );
        });

        const selectionBiRunners = selectionBi?.runners || [];
        const selectionBiRunnersScratched = selectionBiRunners.filter(
          (runner) => runner.scratched
        );
        // update scratchSelections
        selectionBiRunnersScratched.forEach((runner) => {
          if (
            scratchSelections[index] &&
            !scratchSelections[index].includes(runner.runnerId)
          ) {
            scratchSelections[index] = [
              ...scratchSelections[index],
              runner.runnerId
            ];
          }
        });

        const isSelectionValid =
          selectionBiRunnersScratched.length === 0 ||
          selectionBiRunners.length > selectionBiRunnersScratched.length;

        return isSelectionValid;
      })
  );

  return {
    filteredSelections,
    scratchSelections
  };
};

export const selectAllRunners = (
  position: number,
  wagerType: RaceWagerType,
  selections: string[][],
  dispatch: Dispatch,
  runners?: RunnerBI[]
) => {
  const { isKey } = wagerType;
  const positionSelections: string[] = [];
  const allSelections = [...selections];

  if (isKey && selections[0][0]) {
    runners?.forEach((runner: RunnerBI) => {
      if (
        !runner.isScratched &&
        runner.biNumber.toString() !== selections[0][0]
      ) {
        positionSelections.push(runner.biNumber.toString());
      }
    });
  } else {
    runners?.forEach((runner: RunnerBI) => {
      if (!runner.isScratched) {
        positionSelections.push(runner.biNumber.toString());
      }
    });
  }

  allSelections[position as number] = uniq(positionSelections);
  dispatch(setSelectionRequest(allSelections));
};

export const deselectAllRunners = (
  position: number,
  selections: string[][],
  dispatch: Dispatch
) => {
  const allSelections = [...selections];
  allSelections[position as number] = [];
  dispatch(setSelectionRequest(allSelections));
};

export const isAllSelected = (
  selections: string[][],
  wagerType: RaceWagerType,
  numWagerableRunners?: string
) => {
  const { isKey } = wagerType;
  const isTrioWheel = wagerType.type.code === WagerTypeCodesEnum.TRIO_WHEEL;
  const isAllSelectedArray: boolean[] = [];
  const wagerableRunners: number = Number(numWagerableRunners);

  if (isTrioWheel) {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    selections[1] && selections[1].length === wagerableRunners
      ? isAllSelectedArray.push(true)
      : isAllSelectedArray.push(false);
  } else if (isKey) {
    const wagerableNumComparison = selections[0][0]
      ? wagerableRunners - 1
      : wagerableRunners;
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    selections[1] && selections[1].length === wagerableNumComparison
      ? isAllSelectedArray.push(true)
      : isAllSelectedArray.push(false);
  } else {
    for (let i = 0; i < selections.length; i++) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      selections[i as number]?.length === wagerableRunners
        ? isAllSelectedArray.push(true)
        : isAllSelectedArray.push(false);
    }
  }

  return isAllSelectedArray;
};

export const getPosition = (position: number, wagerType: RaceWagerType) => {
  const { isKey } = wagerType;
  const isTrioWheel = wagerType.type.code === WagerTypeCodesEnum.TRIO_WHEEL;
  if (isKey || isTrioWheel) {
    return position - 1;
  }
  return position;
};
